/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button, Form, Row, Col } from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import { ptBR, enUS } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

import { useDispatch, useSelector } from "react-redux";
import {
    setPlant,
    setName,
    setMaintenancePlan,
    setPeriodicity,
    setDuration,
    setExpectedStartDate,
    setDescription,
    resetRoute,
    createRoute,
    setNodes,
    getNewRoute,
    updateRoute,
} from "./features/inspectionRouteSlice";

import dayjs from "dayjs";
import { FaCalendarAlt } from "react-icons/fa";

import { InspectionRouteTree } from "../components/InspectionRouteTree/InspectionRouteTree";
import { DatabaseService } from "../stores/DatabaseService";
import { Periodicity } from "../model/enumerations/periodicity.model";
import { Modal } from "react-bootstrap";
import PhoenixModal from "../components/PhoenixModal";
import { useTranslation } from "react-i18next";
import { getDateLocaleConfig } from "i18n/locale";

registerLocale("pt-BR", ptBR);
registerLocale("en-US", enUS);
interface Props {
    visible: boolean;
    onHide: () => void;
}

export function InspectionRouteFormCreate({ visible, onHide }: Props) {
    const { t, i18n } = useTranslation();
    const tt = (key: string) => t(key, { ns: "translation" });
    const dateLocale = getDateLocaleConfig(i18n.language);
    const dispatch = useDispatch();

    const { data: route, nodes } = useSelector((state: any) => state.inspectionRoute);
    const routesList = useSelector((state: any) => state.inspectionRoute.allRoutes ?? []);
    const isEditing = routesList.some(r => r.id === route.id);

    useEffect(() => {
        if (visible) {
            dispatch(resetRoute());
        }
    }, [visible]);

    const handleSave = async () => {
        if (!route) return;

        let result;

        if (isEditing) {
            result = await dispatch(updateRoute());
            if (updateRoute.fulfilled.match(result)) onHide();
        } else {
            result = await dispatch(createRoute());
            if (createRoute.fulfilled.match(result)) onHide();
        }
    };

    const handlePlantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;

        // Se estiver editando, não permite trocar instalação
        if (isEditing) return;

        // Quando "Selecione uma subestação" é escolhido
        if (!id) {
            dispatch(setPlant(undefined));
            dispatch(setNodes([]));
            dispatch(resetRoute());
            return;
        }

        const selected = powerStations.find(ps => String(ps.id) === id);

        // Caso id inválido ou instalação não encontrada
        if (!selected) {
            dispatch(setPlant(undefined));
            dispatch(setNodes([]));
            dispatch(resetRoute());
            return;
        }

        // Instalação válida, seta e gera rota nova
        dispatch(setPlant(selected));
        dispatch(getNewRoute(selected.id));
    };

    const dbService = DatabaseService.getInstance();
    const powerStations = dbService
        .getPowerStations()
        .sort((a, b) => a.name.localeCompare(b.name));

    const CustomInput = forwardRef<HTMLInputElement, any>(
        ({ value, onClick }, ref) => (
            <div style={{ position: "relative", width: "100%" }}>
                <input
                    ref={ref}
                    type="text"
                    value={value}
                    onClick={onClick}
                    readOnly
                    className="form-control"
                    style={{ paddingRight: "32px" }}
                />
                <FaCalendarAlt
                    onClick={onClick}
                    style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                    }}
                />
            </div>
        )
    );

    const actionTitle = isEditing ? tt("pages.thermography.inspectionRouteFormCreate.actionTitle.edit") : tt("pages.thermography.inspectionRouteFormCreate.actionTitle.create");

    return (
        <PhoenixModal
            show={visible}
            onHide={onHide}
            title={`${actionTitle} ${tt("pages.thermography.inspectionRouteFormCreate.routeTitle")}`}
            size="xl"
            bodyStyle={{ height: "75vh", overflowY: "auto" }}
            footer={
                <>
                    <Button
                        type="button"
                        className="btn btn-phoenix-secondary"
                        onClick={onHide}
                    >
                        {tt("pages.thermography.inspectionRouteFormCreate.cancel")}
                    </Button>

                    <Button
                        type="submit"
                        form="thermoRouteForm"
                        variant='primary'
                    >
                        {tt("pages.thermography.inspectionRouteFormCreate.save")}
                    </Button>
                </>
            }
        >
            {/************************************
     * Formulário superior
     ************************************/}
            <Form
                id="thermoRouteForm"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                }}
            >
                <Row className="mb-3 gx-3">
                    {/* INSTALAÇÃO */}
                    <Form.Group as={Col} md="3">
                        <Form.Label>{tt("pages.thermography.inspectionRouteFormCreate.form.installation")}</Form.Label>

                        <Form.Select
                            value={route.plant?.id ?? ""}
                            disabled={isEditing}
                            onChange={handlePlantChange}
                        >
                            <option value="">{tt("pages.thermography.inspectionRouteFormCreate.form.selectSubstation")}</option>

                            {powerStations.map((p) => (
                                <option key={p.id} value={String(p.id)}>
                                    {p.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group as={Col} md="3">
                        <Form.Label>{tt("pages.thermography.inspectionRouteFormCreate.form.routeCreationId")}</Form.Label>
                        <Form.Control
                            value={route.name ?? ""}
                            onChange={(e) => dispatch(setName(e.target.value))}
                        />
                    </Form.Group>

                    <Form.Group as={Col} md="3">
                        <Form.Label>{tt("pages.thermography.inspectionRouteFormCreate.form.maintenancePlan")}</Form.Label>
                        <Form.Control
                            value={route.maintenancePlan ?? ""}
                            onChange={(e) => dispatch(setMaintenancePlan(e.target.value))}
                        />
                    </Form.Group>

                    <Form.Group as={Col} md="3">
                        <Form.Label>{tt("pages.thermography.inspectionRouteFormCreate.form.periodicity")}</Form.Label>
                        <Form.Select
                            value={route.periodicity ?? ""}
                            onChange={(e) => dispatch(setPeriodicity(e.target.value))}
                        >
                            <option value="">{tt("pages.thermography.inspectionRouteFormCreate.form.selectOption")}</option>

                            {Object.keys(Periodicity).map((key) => (
                                <option key={key} value={key}>
                                    {tt(`pages.thermography.inspectionRoutes.periodicity.${key === "SEMI_ANNUAL" ? "semiAnnual" : key.toLowerCase()}`)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Row>

                <Row className="mb-3 gx-3">
                    <Form.Group as={Col} style={{ flex: "0 0 12.5%" }}>
                        <Form.Label>{tt("pages.thermography.inspectionRouteFormCreate.form.routeDurationDays")}</Form.Label>
                        <Form.Control
                            type="number"
                            value={route.duration ?? 1}
                            min={1}
                            onChange={(e) =>
                                dispatch(setDuration(Number(e.target.value)))
                            }
                        />
                    </Form.Group>

                    <Form.Group as={Col} style={{ flex: "0 0 12.5%" }}>
                        <Form.Label>{tt("pages.thermography.inspectionRouteFormCreate.form.expectedStartDate")}</Form.Label>
                        <DatePicker
                            key={`inspection-route-create-start-${i18n.language}`}
                            locale={dateLocale.datePickerLocale}
                            selected={
                                route.expectedStartDate
                                    ? dayjs(route.expectedStartDate).toDate()
                                    : null
                            }
                            onChange={(date) =>
                                dispatch(
                                    setExpectedStartDate(
                                        date ? date.toISOString() : null
                                    )
                                )
                            }
                            dateFormat={dateLocale.datePickerFormat}
                            customInput={<CustomInput />}
                        />
                    </Form.Group>

                    <Form.Group as={Col} md="9">
                        <Form.Label>{tt("pages.thermography.inspectionRouteFormCreate.form.description")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={route.description ?? ""}
                            onChange={(e) => dispatch(setDescription(e.target.value))}
                        />
                    </Form.Group>
                </Row>
            </Form>

            <div
                style={{
                    height: "45vh",
                    overflowY: "auto",
                    padding: "8px 4px",
                    border: "1px solid var(--phoenix-border-color)",
                    borderRadius: 6,
                }}
            >
                <InspectionRouteTree
                    initialData={nodes}
                    onChange={(newTree) => dispatch(setNodes(newTree))}
                />
            </div>
        </PhoenixModal>

    );
}
